// src/components/Reports.jsx
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { today, PRESETS } from '../utils/dateRange';

function Reports() {
  const [reportType, setReportType] = useState('campaigns');
  const [data, setData] = useState([]);
  const [sort, setSort] = useState({ col: null, dir: 'asc' });
  const [loading, setLoading] = useState(false);
  
  // New state for toggling charts, default is false (hidden)
  const [showCharts, setShowCharts] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [toast, setToast] = useState('');
  
  const [campaigns, setCampaigns] = useState([]);
  const [advertisers, setAdvertisers] = useState([]);
  const { start: todayStart, end: todayEnd } = today();
  const [filters, setFilters] = useState({
    start_date: todayStart,
    end_date: todayEnd,
    campaign_id: '',
    advertiser_id: '',
    group_by: 'campaign'
  });

  const applyPreset = ({ start, end }) =>
    setFilters(f => ({ ...f, start_date: start, end_date: end }));

  useEffect(() => {
    fetchCampaigns();
    fetchAdvertisers();
  }, []);

  const debounceRef = useRef(null);
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(fetchReportData, 300);
    return () => clearTimeout(debounceRef.current);
  }, [reportType, filters]);

  const fetchCampaigns = async () => {
    try {
      const response = await axios.get('/campaigns');
      setCampaigns(response.data.campaigns);
    } catch (error) {
      console.error('Failed to fetch campaigns:', error);
    }
  };

  const fetchAdvertisers = async () => {
    try {
      const response = await axios.get('/advertisers');
      setAdvertisers(response.data.advertisers);
    } catch (error) {
      console.error('Failed to fetch advertisers:', error);
    }
  };

  const fetchReportData = async () => {
    setLoading(true);
    try {
      let endpoint = '/reports/campaigns';
      if (reportType === 'geo') {
        endpoint = '/reports/geo';
      } else if (reportType === 'devices') {
        endpoint = '/reports/devices';
      }

      const response = await axios.get(endpoint, { params: filters });
      setData(response.data.reports || []);
    } catch (error) {
      console.error('Failed to fetch report data:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSort = (col) => {
    setSort(s => s.col === col ? { col, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' });
  };

  const sortedData = React.useMemo(() => {
    if (!sort.col) return data;
    return [...data].sort((a, b) => {
      let av, bv;
      if (sort.col === 'campaign') {
        av = (a.dimension_label || '').toLowerCase();
        bv = (b.dimension_label || '').toLowerCase();
        return sort.dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      av = parseInt(a[sort.col]) || 0;
      bv = parseInt(b[sort.col]) || 0;
      return sort.dir === 'asc' ? av - bv : bv - av;
    });
  }, [data, sort]);

  const SortArrow = ({ col }) => {
    if (sort.col !== col) return <span className="ml-1 text-gray-300">↕</span>;
    return <span className="ml-1">{sort.dir === 'asc' ? '↑' : '↓'}</span>;
  };

  // Totals row — sums raw counts/amounts across all currently-loaded rows,
  // then recomputes the percentage columns from those sums (never averages
  // per-row percentages, which would be wrong whenever rows have different
  // click volumes).
  const summary = React.useMemo(() => {
    if (data.length === 0) return null;
    const sum = (key) => data.reduce((acc, row) => acc + (parseFloat(row[key]) || 0), 0);

    if (reportType === 'campaigns') {
      const clicks = sum('clicks');
      const cost = sum('cost');
      const profit = sum('profit');
      return {
        clicks,
        conversions: sum('conversions'),
        revenue: sum('revenue'),
        cost,
        profit,
        conversion_rate: clicks > 0 ? (sum('conversions') / clicks) * 100 : 0,
        roi: cost > 0 ? (profit / cost) * 100 : 0,
      };
    }
    if (reportType === 'geo') {
      const clicks = sum('clicks');
      return {
        clicks,
        conversions: sum('conversions'),
        revenue: sum('revenue'),
        profit: sum('profit'),
        conversion_rate: clicks > 0 ? (sum('conversions') / clicks) * 100 : 0,
      };
    }
    // devices
    const clicks = sum('clicks');
    return {
      clicks,
      conversions: sum('conversions'),
      conversion_rate: clicks > 0 ? (sum('conversions') / clicks) * 100 : 0,
    };
  }, [data, reportType]);

  const handleExport = async () => {
    try {
      const response = await axios.get('/reports/export', {
        params: { ...filters, format: 'csv' },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `report_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      setToast('Failed to export report');
      setTimeout(() => setToast(''), 3000);
    }
  };

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

  const todayStr = today().start;
  const hasActiveFilters =
    filters.campaign_id || filters.advertiser_id ||
    filters.group_by !== 'campaign' ||
    filters.start_date !== todayStr || filters.end_date !== todayStr;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-3 justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Reports & Analytics</h1>
        <div className="flex gap-3 flex-wrap">
          <button
            type="button"
            onClick={() => setShowFilters(v => !v)}
            className="inline-flex items-center gap-2 px-4 py-2 min-h-[44px] text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 active:bg-gray-100 shadow-sm"
            aria-expanded={showFilters}
            aria-controls="filter-panel"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            {showFilters ? 'Hide Filters' : 'Show Filters'}
            {hasActiveFilters && (
              <span className="inline-block w-2 h-2 bg-blue-500 rounded-full" aria-label="Filters applied" />
            )}
          </button>

          <button
            onClick={() => setShowCharts(!showCharts)}
            className="inline-flex items-center gap-2 px-4 py-2 min-h-[44px] text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 active:bg-gray-100 shadow-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            {showCharts ? 'Hide Charts' : 'Show Charts'}
          </button>

          <button
            type="button"
            onClick={fetchReportData}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 min-h-[44px] text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 active:bg-gray-100 shadow-sm disabled:opacity-50"
            title="Refresh data"
          >
            <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span className="hidden sm:inline">Refresh</span>
          </button>

          <button
            onClick={handleExport}
            className="inline-flex items-center gap-2 px-4 py-2 min-h-[44px] text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 active:bg-green-800 shadow-sm"
          >
            📥 Export CSV
          </button>
        </div>
      </div>

      {/* Report Type Tabs — always visible */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200 overflow-x-auto">
          <nav className="flex -mb-px min-w-max">
            <button
              onClick={() => { setReportType('campaigns'); setSort({ col: null, dir: 'asc' }); }}
              className={`px-4 py-3 text-sm font-medium whitespace-nowrap flex-shrink-0 min-h-[44px] ${
                reportType === 'campaigns'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 active:text-gray-700'
              }`}
            >
              <span className="sm:hidden">Campaigns</span>
              <span className="hidden sm:inline">Campaign Performance</span>
            </button>
            <button
              onClick={() => { setReportType('geo'); setSort({ col: null, dir: 'asc' }); }}
              className={`px-4 py-3 text-sm font-medium whitespace-nowrap flex-shrink-0 min-h-[44px] ${
                reportType === 'geo'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 active:text-gray-700'
              }`}
            >
              Geo<span className="hidden sm:inline">graphic</span>
            </button>
            <button
              onClick={() => { setReportType('devices'); setSort({ col: null, dir: 'asc' }); }}
              className={`px-4 py-3 text-sm font-medium whitespace-nowrap flex-shrink-0 min-h-[44px] ${
                reportType === 'devices'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 active:text-gray-700'
              }`}
            >
              Devices<span className="hidden sm:inline"> & Browsers</span>
            </button>
          </nav>
        </div>

        {/* Filters — collapsible, lives inside the tab card */}
        {showFilters && (
          <div id="filter-panel" className="p-6 border-t border-gray-100">
            <div className="flex flex-wrap gap-2 mb-4">
              {PRESETS.map(p => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => applyPreset(p.get())}
                  className="px-3 py-2 min-h-[44px] text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 active:bg-gray-100"
                >
                  {p.label}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <input
                  type="date"
                  value={filters.start_date}
                  onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                  className="block w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                <input
                  type="date"
                  value={filters.end_date}
                  onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                  className="block w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>

              {reportType === 'campaigns' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Campaign</label>
                    <select
                      value={filters.campaign_id}
                      onChange={(e) => setFilters({ ...filters, campaign_id: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-3 py-2"
                    >
                      <option value="">All Campaigns</option>
                      {campaigns.map(campaign => (
                        <option key={campaign.id} value={campaign.id}>{campaign.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Advertiser</label>
                    <select
                      value={filters.advertiser_id}
                      onChange={(e) => setFilters({ ...filters, advertiser_id: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-3 py-2"
                    >
                      <option value="">All Advertisers</option>
                      {advertisers.map(advertiser => (
                        <option key={advertiser.id} value={advertiser.id}>{advertiser.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Group By</label>
                    <select
                      value={filters.group_by}
                      onChange={(e) => setFilters({ ...filters, group_by: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-3 py-2"
                    >
                      <option value="campaign">Campaign</option>
                      <option value="date">Date</option>
                      <option value="advertiser">Advertiser</option>
                    </select>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Charts (Conditional Rendering based on showCharts) */}
      {loading ? (
        <div className="flex justify-center py-12"><div className="spinner" /></div>
      ) : (
        <>
          {showCharts && reportType === 'campaigns' && data.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Revenue Chart */}
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4">Revenue & Cost</h3>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={data.slice(0, 6)} margin={{ top: 10, right: 8, bottom: 10, left: -10 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="dimension_label" angle={-30} textAnchor="end" height={70} interval={0} tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} width={42} />
                    <Tooltip wrapperStyle={{ fontSize: 12 }} cursor={{ fill: 'rgba(59,130,246,.15)' }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="revenue" fill="#10b981" name="Revenue" />
                    <Bar dataKey="cost" fill="#ef4444" name="Cost" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Profit Chart */}
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4">Profit by Campaign</h3>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={data.slice(0, 6)} margin={{ top: 10, right: 8, bottom: 10, left: -10 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="dimension_label" angle={-30} textAnchor="end" height={70} interval={0} tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} width={42} />
                    <Tooltip wrapperStyle={{ fontSize: 12 }} cursor={{ fill: 'rgba(59,130,246,.15)' }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="profit" fill="#8b5cf6" name="Profit" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Conversion Rate */}
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4">Conversion Rate (%)</h3>
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={data.slice(0, 6)} margin={{ top: 10, right: 8, bottom: 10, left: -10 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="dimension_label" angle={-30} textAnchor="end" height={70} interval={0} tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} width={42} />
                    <Tooltip wrapperStyle={{ fontSize: 12 }} cursor={{ fill: 'rgba(59,130,246,.15)' }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Line type="monotone" dataKey="conversion_rate" stroke="#3b82f6" name="CR %" />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* ROI Chart */}
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4">ROI (%)</h3>
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={data.slice(0, 6)} margin={{ top: 10, right: 8, bottom: 10, left: -10 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="dimension_label" angle={-30} textAnchor="end" height={70} interval={0} tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} width={42} />
                    <Tooltip wrapperStyle={{ fontSize: 12 }} cursor={{ fill: 'rgba(59,130,246,.15)' }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Line type="monotone" dataKey="roi" stroke="#f59e0b" name="ROI %" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {showCharts && reportType === 'geo' && data.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Country Distribution */}
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4">Traffic by Country</h3>
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      data={data.slice(0, 6)}
                      dataKey="clicks"
                      nameKey="country"
                      cx="50%"
                      cy="50%"
                      outerRadius={90}
                      label
                    >
                      {data.slice(0, 6).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip wrapperStyle={{ fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Revenue by Country */}
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4">Revenue by Country</h3>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={data.slice(0, 6)} margin={{ top: 10, right: 8, bottom: 10, left: -10 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="country" angle={-30} textAnchor="end" height={70} interval={0} tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} width={42} />
                    <Tooltip wrapperStyle={{ fontSize: 12 }} cursor={{ fill: 'rgba(59,130,246,.15)' }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="revenue" fill="#10b981" name="Revenue" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {showCharts && reportType === 'devices' && data.length > 0 && (
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-semibold mb-4">Device Performance</h3>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={data.slice(0, 6)} margin={{ top: 10, right: 8, bottom: 10, left: -10 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="device" angle={-30} textAnchor="end" height={70} interval={0} tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} width={42} />
                  <Tooltip wrapperStyle={{ fontSize: 12 }} cursor={{ fill: 'rgba(59,130,246,.15)' }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="clicks" fill="#3b82f6" name="Clicks" />
                  <Bar dataKey="conversions" fill="#10b981" name="Conversions" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Data Table (Always Visible) */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {reportType === 'campaigns' && (
                      <>
                        <th
                          className="px-3 py-2 sm:px-6 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:text-gray-700"
                          onClick={() => toggleSort('campaign')}
                        >
                          {filters.group_by === 'date' ? 'Date' :
                           filters.group_by === 'advertiser' ? 'Advertiser' : 'Campaign'}
                          <SortArrow col="campaign" />
                        </th>
                        <th
                          className="px-3 py-2 sm:px-6 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:text-gray-700"
                          onClick={() => toggleSort('clicks')}
                        >
                          Clicks<SortArrow col="clicks" />
                        </th>
                        <th
                          className="px-3 py-2 sm:px-6 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:text-gray-700"
                          onClick={() => toggleSort('conversions')}
                        >
                          Conversions<SortArrow col="conversions" />
                        </th>
                        <th className="px-3 py-2 sm:px-6 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase">CR %</th>
                        <th className="px-3 py-2 sm:px-6 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase">Revenue</th>
                        <th className="px-3 py-2 sm:px-6 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase">Cost</th>
                        <th className="px-3 py-2 sm:px-6 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase">Profit</th>
                        <th className="px-3 py-2 sm:px-6 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase">ROI %</th>
                      </>
                    )}
                    {reportType === 'geo' && (
                      <>
                        <th className="px-3 py-2 sm:px-6 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase">Country</th>
                        <th className="px-3 py-2 sm:px-6 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase">Clicks</th>
                        <th className="px-3 py-2 sm:px-6 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase">Conversions</th>
                        <th className="px-3 py-2 sm:px-6 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase">CR %</th>
                        <th className="px-3 py-2 sm:px-6 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase">Revenue</th>
                        <th className="px-3 py-2 sm:px-6 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase">Profit</th>
                      </>
                    )}
                    {reportType === 'devices' && (
                      <>
                        <th className="px-3 py-2 sm:px-6 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase">Device</th>
                        <th className="px-3 py-2 sm:px-6 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase">OS</th>
                        <th className="px-3 py-2 sm:px-6 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase">Browser</th>
                        <th className="px-3 py-2 sm:px-6 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase">Clicks</th>
                        <th className="px-3 py-2 sm:px-6 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase">Conversions</th>
                        <th className="px-3 py-2 sm:px-6 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase">CR %</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sortedData.map((row, index) => (
                    <tr key={index}>
                      {reportType === 'campaigns' && (
                        <>
                          <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {row.dimension_label}
                          </td>
                          <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-sm text-gray-900">{row.clicks}</td>
                          <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-sm text-gray-900">{row.conversions}</td>
                          <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-sm text-gray-900">
                            {parseFloat(row.conversion_rate || 0).toFixed(2)}%
                          </td>
                          <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-sm font-medium text-green-600">
                            ${parseFloat(row.revenue || 0).toFixed(2)}
                          </td>
                          <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-sm font-medium text-red-600">
                            ${parseFloat(row.cost || 0).toFixed(2)}
                          </td>
                          <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-sm font-bold">
                            <span className={parseFloat(row.profit) >= 0 ? 'text-green-600' : 'text-red-600'}>
                              ${parseFloat(row.profit || 0).toFixed(2)}
                            </span>
                          </td>
                          <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-sm text-gray-900">
                            {parseFloat(row.roi || 0).toFixed(2)}%
                          </td>
                        </>
                      )}
                      {reportType === 'geo' && (
                        <>
                          <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {row.country}
                          </td>
                          <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-sm text-gray-900">{row.clicks}</td>
                          <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-sm text-gray-900">{row.conversions}</td>
                          <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-sm text-gray-900">
                            {parseFloat(row.conversion_rate || 0).toFixed(2)}%
                          </td>
                          <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-sm font-medium text-green-600">
                            ${parseFloat(row.revenue || 0).toFixed(2)}
                          </td>
                          <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-sm font-bold text-purple-600">
                            ${parseFloat(row.profit || 0).toFixed(2)}
                          </td>
                        </>
                      )}
                      {reportType === 'devices' && (
                        <>
                          <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {row.device}
                          </td>
                          <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-sm text-gray-900">{row.os}</td>
                          <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-sm text-gray-900">{row.browser}</td>
                          <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-sm text-gray-900">{row.clicks}</td>
                          <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-sm text-gray-900">{row.conversions}</td>
                          <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-sm text-gray-900">
                            {parseFloat(row.conversion_rate || 0).toFixed(2)}%
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
                {summary && (
                  <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                    <tr>
                      {reportType === 'campaigns' && (
                        <>
                          <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-sm font-bold text-gray-900">Total</td>
                          <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-sm font-bold text-gray-900">{summary.clicks}</td>
                          <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-sm font-bold text-gray-900">{summary.conversions}</td>
                          <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                            {summary.conversion_rate.toFixed(2)}%
                          </td>
                          <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-sm font-bold text-green-700">
                            ${summary.revenue.toFixed(2)}
                          </td>
                          <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-sm font-bold text-red-700">
                            ${summary.cost.toFixed(2)}
                          </td>
                          <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-sm font-bold">
                            <span className={summary.profit >= 0 ? 'text-green-700' : 'text-red-700'}>
                              ${summary.profit.toFixed(2)}
                            </span>
                          </td>
                          <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                            {summary.roi.toFixed(2)}%
                          </td>
                        </>
                      )}
                      {reportType === 'geo' && (
                        <>
                          <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-sm font-bold text-gray-900">Total</td>
                          <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-sm font-bold text-gray-900">{summary.clicks}</td>
                          <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-sm font-bold text-gray-900">{summary.conversions}</td>
                          <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                            {summary.conversion_rate.toFixed(2)}%
                          </td>
                          <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-sm font-bold text-green-700">
                            ${summary.revenue.toFixed(2)}
                          </td>
                          <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-sm font-bold text-purple-700">
                            ${summary.profit.toFixed(2)}
                          </td>
                        </>
                      )}
                      {reportType === 'devices' && (
                        <>
                          <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-sm font-bold text-gray-900" colSpan={3}>
                            Total
                          </td>
                          <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-sm font-bold text-gray-900">{summary.clicks}</td>
                          <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-sm font-bold text-gray-900">{summary.conversions}</td>
                          <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                            {summary.conversion_rate.toFixed(2)}%
                          </td>
                        </>
                      )}
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </>
      )}

      {!loading && data.length === 0 && (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <p className="text-gray-500">No data available for the selected filters.</p>
        </div>
      )}

      {toast && (
        <div className="fixed bottom-4 right-4 bg-gray-800 text-white px-4 py-2 rounded-md shadow-lg text-sm z-50">
          {toast}
        </div>
      )}
    </div>
  );
}

export default Reports;